/**
 * asnOverviewKpiRequestBuilder.ts
 *
 * Pure helper that converts the ASN Gap filter bar's raw UI selections
 * (KPIFilterDropdownItem[] / DeliveryDateRange / free-text case quantity
 * strings) into the exact `ASNOverviewKpiRequest` payload shape expected by
 * `POST /api/asn/overview/kpis`.
 *
 * Kept isolated (and free of React state/hooks) so it can be unit tested in
 * isolation and reused identically for the initial screen-load request, the
 * Apply-triggered request and the Reset-triggered request — there is a single
 * source of truth for how the payload is assembled, so all three call sites
 * are guaranteed to agree on shape/typing.
 */

import { KPIFilterDropdownItem } from '../../../../AnalyticsHub/components/KPIFilterDropdown';
import { DeliveryDateRange } from '../../../components/DeliveryDateRangeFilter';
import {
  ASNBottomPerformingRequest,
  ASNGapLocationHierarchy,
  ASNLoadDetailsRequest,
  ASNLocationDetailsRequest,
  ASNNonAsnLoadTrendRequest,
  ASNOverviewKpiRequest,
  ASNTrendTimeframeGrain,
  AsnLocationGrainKey,
  LoadsVsNonAsnChartRequest,
  LoadsVsNonAsnChartSortBy,
} from '../api/asnOverviewKpiApi';
import { OverviewHierarchyLevel, OverviewTimeframeLevel } from '../components/Overview';

export interface AsnOverviewKpiRequestInputs {
  regions: KPIFilterDropdownItem[];
  markets: KPIFilterDropdownItem[];
  shippingLocations: KPIFilterDropdownItem[];
  receivingLocations: KPIFilterDropdownItem[];
  years: KPIFilterDropdownItem[];
  periods: KPIFilterDropdownItem[];
  weeks: KPIFilterDropdownItem[];
  deliveryDateRange: DeliveryDateRange;
  /** Raw text field values (may contain non-numeric input mid-typing). */
  caseQtyMin: string;
  caseQtyMax: string;
}

const idsOf = (items: KPIFilterDropdownItem[] | undefined): string[] =>
  items ? items.map((item) => String(item.id).trim()).filter((id) => id.length > 0) : [];

/** Period dropdown ids are formatted like "P3" — strip the leading "P". */
const periodNumberOf = (item: KPIFilterDropdownItem): number =>
  Number(String(item.id).replace(/^P/i, ''));

/**
 * Week dropdown items represent a "week in year" value (the filter is
 * labeled WEEK IN YEAR on screen). Every week item built by AsnGapDashboard
 * (via the weekOptions memo, buildWeekItems, or the reset defaults) carries
 * a `weekInYearNum` field — prefer that, falling back to parsing the
 * "W37 (P3W1)" display text for robustness.
 */
const weekInYearNumberOf = (item: KPIFilterDropdownItem): number => {
  const fromField = Number((item as { weekInYearNum?: unknown }).weekInYearNum);
  if (Number.isFinite(fromField)) return fromField;
  const match = String(item.displayText).match(/^W(\d+)/i);
  return match ? Number(match[1]) : NaN;
};

const parseCaseQty = (raw: string | undefined): number | undefined => {
  if (raw === undefined || raw === null) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Builds the `/api/asn/overview/kpis` request payload from the ASN Gap
 * filter bar's currently-selected (already-committed) values.
 *
 * - An empty `years`/`periods`/`weeks`/`regions`/etc. selection means "All"
 *   for that dimension per the existing ASN Gap filter convention, and is
 *   sent as an empty array (no restriction) — never a hardcoded default.
 * - Year values are always converted to numbers (dropdown ids are strings).
 */
export const buildAsnOverviewKpiRequest = ({
  regions = [],
  markets = [],
  shippingLocations = [],
  receivingLocations = [],
  years = [],
  periods = [],
  weeks = [],
  deliveryDateRange = {},
  caseQtyMin,
  caseQtyMax,
}: AsnOverviewKpiRequestInputs = {} as any): ASNOverviewKpiRequest => {
  const parsedCaseQuantityMin = parseCaseQty(caseQtyMin);
  const parsedCaseQuantityMax = parseCaseQty(caseQtyMax);

  return {
    shippingRegions: idsOf(regions),
    shippingMarkets: idsOf(markets),
    shippingLocationIds: idsOf(shippingLocations),
    receivingLocationIds: idsOf(receivingLocations),
    years: (years || []).map((y) => Number(y?.id)).filter((n) => Number.isFinite(n)),
    periods: (periods || []).map(periodNumberOf).filter((n) => Number.isFinite(n)),
    weeks: (weeks || []).map(weekInYearNumberOf).filter((n) => Number.isFinite(n)),
    dateFrom: deliveryDateRange?.start ?? '',
    dateTo: deliveryDateRange?.end ?? '',
    ...(parsedCaseQuantityMin !== undefined ? { caseQuantityMin: parsedCaseQuantityMin } : {}),
    ...(parsedCaseQuantityMax !== undefined ? { caseQuantityMax: parsedCaseQuantityMax } : {}),
  };
};

/**
 * Maps the Overview tab's Chart Configuration -> Location Hierarchy selection
 * ('region' | 'market' | 'location') to the backend's ASNGapLocationHierarchy
 * enum ('REGION' | 'MARKET' | 'LOCATION'). Defaults to 'REGION' per the
 * Bottom Performing Non-ASN contract (matches AsnGapDashboard's own default
 * `chartHierarchyLevel` state of 'region').
 */
export const mapChartHierarchyToLocationHierarchy = (
  hierarchyLevel: OverviewHierarchyLevel | undefined,
): ASNGapLocationHierarchy => {
  switch (hierarchyLevel) {
    case 'market':
      return 'MARKET';
    case 'location':
      return 'LOCATION';
    case 'region':
    default:
      return 'REGION';
  }
};

export interface AsnBottomPerformingRequestInputs extends AsnOverviewKpiRequestInputs {
  /** Overview tab's Chart Configuration -> Location Hierarchy selection. */
  locationHierarchy: OverviewHierarchyLevel;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
}

/**
 * Builds the `/api/asn/bottom-performing-non-asn` request payload. Shares
 * every filter field with `buildAsnOverviewKpiRequest` (same source of
 * truth) and adds the ranking-specific `locationHierarchy` (mapped from the
 * Chart Configuration control) plus the sort parameters.
 */
export const buildAsnBottomPerformingRequest = ({
  locationHierarchy,
  sortColumn = 'NON_ASN_LOADS',
  sortDirection = 'DESC',
  ...kpiInputs
}: AsnBottomPerformingRequestInputs): ASNBottomPerformingRequest => ({
  ...buildAsnOverviewKpiRequest(kpiInputs),
  locationHierarchy: mapChartHierarchyToLocationHierarchy(locationHierarchy),
  sortColumn,
  sortDirection,
});

/**
 * Maps the Overview tab's Chart Configuration -> Timeframe selection
 * ('year' | 'period' | 'week') to the backend's ASNTrendTimeframeGrain enum
 * ('YEAR' | 'PERIOD' | 'WEEK'). Defaults to 'PERIOD' per the Non-ASN Load %
 * Trend contract (matches AsnGapDashboard's own default `chartTimeframeGrain`
 * state of 'period').
 */
export const mapChartTimeframeToTimeframeGrain = (
  timeframeLevel: OverviewTimeframeLevel | undefined,
): ASNTrendTimeframeGrain => {
  switch (timeframeLevel) {
    case 'week':
      return 'WEEK';
    case 'year':
      return 'YEAR';
    case 'period':
    default:
      return 'PERIOD';
  }
};

export interface AsnNonAsnLoadTrendRequestInputs extends AsnOverviewKpiRequestInputs {
  /** Overview tab's Chart Configuration -> Timeframe selection. */
  timeframeGrain: OverviewTimeframeLevel;
}

/**
 * Builds the `/api/asn/trend/non-asn-load-trend` request payload. Shares
 * every filter field with `buildAsnOverviewKpiRequest` (same source of
 * truth) and adds the trend-specific `timeframeGrain` (mapped from the
 * Chart Configuration control).
 */
export const buildAsnNonAsnLoadTrendRequest = ({
  timeframeGrain,
  ...kpiInputs
}: AsnNonAsnLoadTrendRequestInputs): ASNNonAsnLoadTrendRequest => ({
  ...buildAsnOverviewKpiRequest(kpiInputs),
  timeframeGrain: mapChartTimeframeToTimeframeGrain(timeframeGrain),
});

export interface AsnLoadDetailsRequestInputs extends AsnOverviewKpiRequestInputs {
  /** 1-based current page. */
  pageNumber: number;
  pageSize: number;
  /** Backend-supported sort field name, e.g. "receivedDate", "cases". */
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
  searchText: string;
}

/**
 * Builds the `/api/asn/load-details/list` request payload. Shares every
 * filter field with `buildAsnOverviewKpiRequest` (same source of truth) and
 * adds the list-specific pagination/sort/search fields, which are owned by
 * AsnGapDashboard as their own state (independent of the filter bar) since
 * they change from in-table interactions (page/page-size/sort/search)
 * rather than the Apply/Reset filter flow.
 */
export const buildAsnLoadDetailsRequest = ({
  pageNumber,
  pageSize,
  sortBy,
  sortDirection,
  searchText,
  ...kpiInputs
}: AsnLoadDetailsRequestInputs): ASNLoadDetailsRequest => ({
  ...buildAsnOverviewKpiRequest(kpiInputs),
  pageNumber,
  pageSize,
  sortBy,
  sortDirection,
  searchText,
});

export interface AsnLocationDetailsRequestInputs extends AsnOverviewKpiRequestInputs {
  perspective: 'shipping' | 'receiving';
  /** Committed Chart Configuration grain selections, in display order. */
  grainSelections: AsnLocationGrainKey[];
  /** Backend-supported sort field — a grain key or a metric key. */
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
  searchQuery: string;
  /** Zero-based current page. */
  pageNumber: number;
  pageSize: number;
}

const LOCATION_DETAILS_GRAIN_ORDER: AsnLocationGrainKey[] = [
  'region',
  'market',
  'location',
  'locationType',
  'year',
  'period',
  'week',
];

const normalizeLocationDetailsGrains = (
  grainSelections: readonly AsnLocationGrainKey[],
): AsnLocationGrainKey[] => {
  const selected = new Set(grainSelections);
  const merged = new Set<AsnLocationGrainKey>([
    ...LOCATION_DETAILS_GRAIN_ORDER,
    ...Array.from(selected),
  ]);
  return LOCATION_DETAILS_GRAIN_ORDER.filter((grain) => merged.has(grain));
};

/**
 * Builds the `/api/asn/location-details` request payload. Shares every
 * filter field with `buildAsnOverviewKpiRequest` (same source of truth —
 * `years` always reflects the currently selected Year filter, never a
 * hardcoded value) and adds the Location Summary tab's own
 * perspective/grainSelections/sort/search/pagination fields, which are
 * owned by the Location Summary tab itself (Chart Configuration panel +
 * in-table interactions), independent of the shared filter bar's
 * Apply/Reset flow.
 */
export const buildAsnLocationDetailsRequest = ({
  perspective,
  grainSelections,
  sortBy,
  sortDirection,
  searchQuery,
  pageNumber,
  pageSize,
  ...kpiInputs
}: AsnLocationDetailsRequestInputs): ASNLocationDetailsRequest => ({
  ...buildAsnOverviewKpiRequest(kpiInputs),
  perspective,
  grainSelections: normalizeLocationDetailsGrains(grainSelections),
  sortBy,
  sortDirection,
  searchQuery,
  pageNumber,
  pageSize,
});

export interface AsnLoadsVsNonAsnChartRequestInputs extends AsnOverviewKpiRequestInputs {
  sortBy: LoadsVsNonAsnChartSortBy;
  sortDirection: 'ASC' | 'DESC';
  searchQuery: string;
  /** Overview tab's Chart Configuration → Location Hierarchy selection.
   * Sent with every request so the backend groups rows by the correct grain. */
  locationHierarchy: OverviewHierarchyLevel;
  parentGrain?: string;
  parentGrainVals?: Record<string, string>;
}

/**
 * Builds the `/asn/loads-vs-non-asn/chart` request payload. Shares
 * every filter field with `buildAsnOverviewKpiRequest` (same source of
 * truth — `years` always reflects the currently selected Year filter,
 * never a hardcoded value) and adds the chart's own sort/search fields
 * plus the `locationHierarchy` grain (mapped from the Chart Configuration
 * control), which is sent on every call so the backend always groups rows
 * by the currently-selected grain.
 */
export const buildAsnLoadsVsNonAsnChartRequest = ({
  sortBy,
  sortDirection,
  searchQuery,
  locationHierarchy,
  parentGrain,
  parentGrainVals,
  ...kpiInputs
}: AsnLoadsVsNonAsnChartRequestInputs): LoadsVsNonAsnChartRequest => ({
  ...buildAsnOverviewKpiRequest(kpiInputs),
  sortBy,
  sortDirection,
  searchQuery,
  locationHierarchy: mapChartHierarchyToLocationHierarchy(locationHierarchy),
  parentGrain,
  parentGrainVals,
});

export default buildAsnOverviewKpiRequest;
